import { ReactNode } from "react";

import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import {
  DutyScheduleCellHighlight,
  ROW_HEADER_PROPS,
  useDutyScheduleTableStyles,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout.styles";

/**
 * One column of a Dienstplan table — modelled after MUI X `DataGrid`'s `GridColDef` (field, header,
 * width, `renderCell`) so a future move to `DataGrid` only has to translate this shape, not redesign
 * every call site from scratch. The first column of a table is its sticky one (Mitarbeiter:in or
 * Datum, depending on the table) — there is no separate "row header" concept, it is simply
 * `columns[0]`.
 */
export interface DutyScheduleTableColumn<TRow> {
  field: string;
  headerName: ReactNode;
  width: number;
  align?: "left" | "right";
  // The border/padding treatment Mitarbeiterübersicht/Verteilung AW/BG's numeric columns share —
  // Daten erfassen's date grid does not use it. The shell owns the concrete values.
  bordered?: boolean;
  // A column whose body cells are always bold (a sticky column's label, or a Summe column).
  bold?: boolean;
  // Static header-cell styling layered under the shell's own header tint/width/border.
  headerSx?: SystemStyleObject<Theme>;
  // Static per-column styling that never depends on the row (e.g. Daten erfassen's editable-cell
  // padding) — layered under `bordered`/`bold`/`getHighlight`'s semantic treatment.
  cellSx?: SystemStyleObject<Theme>;
  // What this row's cell in this column *means* — DutyScheduleTableLayout owns what that actually
  // looks like, so a table only ever says "this is the totals row", never the color for it.
  getHighlight?: (row: TRow) => DutyScheduleCellHighlight;
  renderCell: (row: TRow) => ReactNode;
}

// `columns[0]` renders as the sticky column — see DutyScheduleTableColumn. A tuple type so callers
// (and this component) get that guarantee from the compiler instead of a runtime check.
export type DutyScheduleTableColumns<TRow> = [DutyScheduleTableColumn<TRow>, ...Array<DutyScheduleTableColumn<TRow>>];

interface Props<TRow> {
  ariaLabel: string;
  columns: DutyScheduleTableColumns<TRow>;
  data: TRow[];
  getRowId: (row: TRow) => string;
  // Defaults to true — only a totals row opts out.
  isRowHoverable?: (row: TRow) => boolean;
}

/**
 * The scaffolding shared by every Dienstplan table (Daten erfassen, Mitarbeiterübersicht, Verteilung
 * AW/BG): a sticky-header, sticky-first-column table pinned to the sum of its own column widths so a
 * fixed layout can't hand that space back and stretch the columns.
 */
export function DutyScheduleTableLayout<TRow>({ ariaLabel, columns, data, getRowId, isRowHoverable }: Props<TRow>) {
  const classes = useDutyScheduleTableStyles();
  const [stickyColumn, ...dataColumns] = columns;

  const tableWidth = columns.reduce((sum, column) => sum + column.width, 0);

  return (
    <TableContainer component={Paper} variant="outlined" sx={classes.tableContainer}>
      <Table size="small" stickyHeader aria-label={ariaLabel} sx={classes.table(tableWidth)}>
        <TableHead>
          <TableRow>
            <TableCell
              align={stickyColumn.align}
              sx={[classes.headerCell, classes.headerRowHeaderCell(stickyColumn.width), stickyColumn.headerSx ?? {}]}
            >
              {stickyColumn.headerName}
            </TableCell>
            {dataColumns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align}
                sx={[
                  classes.headerCell,
                  { width: column.width },
                  column.bordered ? classes.borderedCell : {},
                  column.headerSx ?? {},
                ]}
              >
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow key={getRowId(row)} hover={isRowHoverable?.(row) ?? true}>
              <TableCell
                {...ROW_HEADER_PROPS}
                align={stickyColumn.align}
                sx={[
                  classes.rowHeaderCell(stickyColumn.width),
                  stickyColumn.bold ? classes.boldCell : {},
                  stickyColumn.cellSx ?? {},
                  classes.cellHighlight(stickyColumn.getHighlight?.(row)),
                ]}
              >
                {stickyColumn.renderCell(row)}
              </TableCell>
              {dataColumns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align}
                  sx={[
                    column.bordered ? classes.borderedCell : {},
                    column.bold ? classes.boldCell : {},
                    column.cellSx ?? {},
                    classes.cellHighlight(column.getHighlight?.(row)),
                  ]}
                >
                  {column.renderCell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
